import type { StatusEvidence } from '@/hooks/useStatusEvidence';
import { normalizeStatus } from '@/constants/status';

interface EvidenceDisplay {
  readonly label: string;
  readonly badgeClass: string;
  readonly nextStep: string;
}

/** One display rule for header, dashboard badges, and dashboard actions. */
export function statusEvidenceDisplay(evidence: StatusEvidence): EvidenceDisplay {
  const display = (label: string, badgeClass: string, nextStep = 'View submission'): EvidenceDisplay => ({ label, badgeClass, nextStep });
  if (!evidence.snapshot) return display(evidence.loading ? 'Checking status…' : 'Status unavailable', 'badge-draft');
  const { stages, status } = evidence.snapshot;
  const normalized = normalizeStatus(status);
  const stage = (key: string) => stages.find((item) => item.key === key);
  const agency = stage('submitted-to-agency');
  if (agency?.state === 'error') return display('Rejected', 'badge-failed', 'Review agency response');
  if (agency?.state === 'active' || agency?.state === 'done') {
    return display(normalized === 'Approved' ? 'Accepted by agency' : 'Submitted', 'badge-complete');
  }
  const compiled = stage('compiled');
  if (compiled?.state === 'error') return display('Compilation failed', 'badge-failed', 'Review errors');
  if (normalized === 'Processing') return display('Compiling…', 'badge-processing');
  if (compiled?.code === 'unavailable') return display('Package status unavailable', 'badge-draft');
  if (compiled?.code === 'partial') return display('Partial package', 'badge-confirm', 'Review and compile');
  if (compiled?.code === 'stale') return display('Package out of date', 'badge-confirm', 'Validate and compile');
  if (compiled?.code === 'legacy') return display('Package needs review', 'badge-confirm', 'Review and compile');
  if (compiled?.state === 'active') return display('Package needs review', 'badge-confirm', 'Review and compile');
  const validation = stage('validated');
  if (compiled?.state === 'done') {
    return display(validation?.state === 'done' ? 'Compiled' : 'Compiled · validation needed',
      validation?.state === 'done' ? 'badge-complete' : 'badge-confirm');
  }
  if (stage('documents')?.state !== 'done') return display('Draft', 'badge-draft', 'Upload documents');
  if (stage('classified')?.state !== 'done') return display('Classify', 'badge-classify', 'Review placements');
  if (stage('confirmed')?.state !== 'done') return display('Confirm', 'badge-confirm', 'Review placements');
  if (validation?.code === 'unavailable') return display('Validation unavailable', 'badge-draft');
  if (validation?.code === 'stale') return display('Validation out of date', 'badge-confirm', 'Validate and compile');
  if (validation?.state === 'error' || validation?.state === 'active') return display('Validation errors', 'badge-failed', 'Review validation');
  if (validation?.state === 'done') return display('Ready to compile', 'badge-ready', 'Compile submission');
  return display('Ready for validation', 'badge-ready', 'Validate and compile');
}
